import { useMemo } from 'react';
import { Box, useMediaQuery, useTheme } from '@mui/material';
import {
  axisClasses,
  BarChart,
  ChartsLegend,
  type ChartsLegendProps,
} from '@mui/x-charts';
import { labelMarkClasses } from '@mui/x-charts/ChartsLabel';
import { bloxMarketingColors } from '../../../../themes/constants';
import {
  CHART_HEIGHT,
  CHART_MARGIN_TOP_DESK,
  CHART_MARGIN_TOP_MOBI,
} from 'components/Dashboard/shared/constants';
import {
  getIsOldSchoolStacked,
  parseBarChartConfig,
  parseBarChartData,
  parseStackedBarChartData,
} from './utils';
import { useWidgetContext } from 'components/Dashboard/Widget';

const CustomLegend = (props: ChartsLegendProps) => {
  return (
    <div data-testid="bar-chart-legend">
      <ChartsLegend {...props} />
    </div>
  );
};

const BloxBarChart = () => {
  const { widget, data } = useWidgetContext();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  const { isStacked, yDataKeys } = parseBarChartConfig(widget);

  const parsedData = useMemo(() => {
    if (!widget || !data?.results?.length) return;

    if (isStacked && !getIsOldSchoolStacked({ data, isStacked, yDataKeys })) {
      return parseStackedBarChartData(data, widget);
    }

    // If the chart is not stacked, parse the data normally.
    return parseBarChartData(data, widget);
  }, [widget, data, isStacked, yDataKeys]);

  const { isHorizontal, xAxis, series } = parsedData || {};

  return (
    <Box
      data-testid={`bar-chart-${isStacked ? 'stacked' : 'grouped'}-${isHorizontal ? 'horizontal' : 'vertical'}`}
      sx={{ width: '100%' }}
    >
      <BarChart
        height={CHART_HEIGHT}
        margin={{
          left: isMobile ? 50 : isTablet ? 56 : 72,
          right: isMobile ? 8 : 16,
          top: isMobile ? CHART_MARGIN_TOP_MOBI : CHART_MARGIN_TOP_DESK,
          bottom: isMobile ? 8 : 40,
        }}
        colors={Object.values(bloxMarketingColors)}
        slots={{
          legend: CustomLegend,
        }}
        hideLegend={isMobile}
        slotProps={{
          legend: {
            direction: 'horizontal',
            position: {
              vertical: 'top',
              horizontal: 'center',
            },
            sx: {
              gap: isMobile ? 8 : 12,
              padding: isMobile ? 0 : 8,
              [`.${labelMarkClasses}`]: {
                fontSize: isMobile ? 11 : 13,
              },
            },
          },
        }}
        sx={{
          [`.${axisClasses.left} .${axisClasses.label}`]: {
            transform: isMobile ? 'translate(-12px, 0)' : 'translate(-20px, 0)',
          },
          [`.${axisClasses.left} .${axisClasses.tickLabel}`]: {
            fontSize: isMobile ? '0.7rem' : '0.875rem',
          },
          [`.${axisClasses.bottom} .${axisClasses.tickLabel}`]: {
            fontSize: isMobile ? '0.7rem' : '0.875rem',
          },
          '.MuiChartsLegend-series': {
            '.MuiChartsLegend-mark': {
              rx: 100,
              maxWidth: isMobile ? 16 : 20,
            },
          },
        }}
        // Configuration for vertical/horizontal layouts.
        {...(isHorizontal
          ? { series: xAxis || [], yAxis: series || [], layout: 'horizontal' }
          : { xAxis: xAxis || [], series: series || [], layout: 'vertical' })}
      />
    </Box>
  );
};

export default BloxBarChart;
